/* eslint-disable react/require-default-props */
import MuiAutocomplete, {
  type AutocompleteProps as _MuiAutocompleteProps,
} from "@material-ui/lab/Autocomplete";
import { path } from "ramda";
import { Controller, type FieldError, useFormContext } from "react-hook-form";

type BoolOrUndefined = boolean | undefined;
type MuiAutocompleteProps<T> = _MuiAutocompleteProps<
  T,
  BoolOrUndefined,
  BoolOrUndefined,
  BoolOrUndefined
>;
type RHFAutocompleteProps<T> = Partial<
  Omit<MuiAutocompleteProps<T>, "renderInput" | "options">
> & {
  name: string;
  renderInput: (
    errStr?: string | null,
  ) => MuiAutocompleteProps<T>["renderInput"];
  defaultValue?: any;
  options: readonly T[];
};

const RHFAutocomplete = <T extends Record<string, any>>({
  name,
  renderInput,
  defaultValue = null,
  options,
  ...otherProps
}: RHFAutocompleteProps<T>) => {
  const {
    control,
    getValues,
    formState: { errors, touchedFields, isSubmitted },
  } = useFormContext();
  const error = path(name.split("."), errors) as FieldError;
  const touched = path(name.split("."), touchedFields);
  const errStr = touched || isSubmitted ? error?.message : null;

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={getValues(name) ?? defaultValue}
      render={({ field }) => (
        <MuiAutocomplete
          {...field}
          renderInput={renderInput(errStr)}
          onChange={(_e, option) => {
            field.onChange(option);
          }}
          options={options as T[]}
          {...otherProps}
        />
      )}
    />
  );
};

export default RHFAutocomplete;
